import { defaultTrace } from "./tracing.js";
import { utcNow } from "./subscribers/util.js";
export * from "./lib.js";
export class Logger {
    options = {
        trace: defaultTrace,
        tags: [],
        global: {},
    };
    constructor(opts) {
        this.options = { ...this.options, ...opts };
    }
    withContext = (options) => {
        this.options = { ...this.options, ...options };
    };
    withGlobalFields = (options) => {
        this.options = { ...this.options, global: { ...this.options.global, ...options } };
    };
    /** A logger that adds `fields` to every event and span; this logger is left unchanged. */
    withFields = (fields) => new Logger({ ...this.options, global: { ...this.options.global, ...fields } });
    get trace() {
        return this.options.trace;
    }
    error(message, fieldsOrError, fields) {
        const error = fieldsOrError instanceof Error ? fieldsOrError : undefined;
        const flds = {
            ...(typeof fieldsOrError === "string" || fieldsOrError instanceof Error
                ? { error: serializeError(fieldsOrError) }
                : typeof fieldsOrError === "object"
                    ? fieldsOrError
                    : {}),
            ...fields,
        };
        return this.levelEvent(this.error, "error", message, flds, error);
    }
    warn(message, fields) {
        const source = this.captureSource(this.warn);
        return this.levelEvent(this.warn, "warn", message, { source, ...fields });
    }
    info(message, fields) {
        const source = this.captureSource(this.info);
        return this.levelEvent(this.info, "info", message, { source, ...fields });
    }
    log(message, fields) {
        const source = this.captureSource(this.log);
        return this.levelEvent(this.log, "info", message, { source, ...fields });
    }
    debug(message, fields) {
        const source = this.captureSource(this.debug);
        return this.levelEvent(this.debug, "debug", message, { source, ...fields });
    }
    /** Start a new span */
    span = (level, name, fields) => {
        const span = this.trace.enter({
            id: crypto.randomUUID(),
            name,
            level,
            timestamp: utcNow().valueOf(),
            fields: { ...fields },
        });
        let exited = false;
        return {
            exit: () => {
                if (exited)
                    return;
                exited = true;
                this.trace.exit(span);
            },
        };
    };
    event = (event) => this.trace.event(this.applyContext(event));
    enter = (span) => this.trace.enter(this.applyContext(span));
    exit = (span) => this.trace.exit(this.applyContext(span));
    levelEvent(target, level, message, fields, error) {
        return this.trace.event(this.applyContext({
            level,
            message,
            timestamp: utcNow().valueOf(),
            fields: { ...fields, source: this.captureSource(target) },
            error,
        }));
    }
    applyContext = (event) => {
        return {
            ...event,
            fields: {
                ...event.fields,
                ...this.options?.global,
                tags: [...(this.options?.tags ?? []), ...(event.fields?.tags ?? [])],
            },
        };
    };
    captureSource = (target) => {
        const err = new Error();
        Error.captureStackTrace?.(err, target);
        const stack = err.stack?.split("\n") ?? [];
        const frame = stack.slice(1)[0];
        return /\((.*)\)/.exec(frame ?? "")?.[1]?.trim();
    };
}
export const logger = new Logger({ tags: [] });
export const trace = defaultTrace;
/** Swallow errors, log them and return undefined */
export const tryCatch = (name, fn, ...args) => {
    const log = (e) => {
        if (e instanceof Error) {
            logger.error(name, e, { args });
        }
        else {
            logger.error(name, { error: e, args });
        }
    };
    try {
        const result = fn(...args);
        if (result instanceof Promise) {
            return result.catch((error) => {
                log(error);
                return undefined;
            });
        }
        return result;
    }
    catch (error) {
        log(error);
        return undefined;
    }
};
const serializeError = (err) => {
    if (typeof err === "string")
        return { message: err };
    if (err instanceof Error) {
        const props = Object.getOwnPropertyNames(err)
            .filter((key) => !["name", "message", "stack"].includes(key))
            .reduce((o, k) => {
            o[k] = err[k];
            return o;
        }, {});
        return { name: err.name, message: err.message, stack: err.stack, ...props };
    }
    return { message: String(err) };
};
//# sourceMappingURL=index.js.map